/* ica-resources.js — TDS Library + Case Studies interactions + event firing.
   Loaded with `defer` by the sections that need it. Vanilla JS, no jQuery.

   Wires:
     - TDS gate form expand/cancel/submit (guest flow; logged-in skips gate)
     - TDS download direct click (logged-in)
     - TDS library search (client-side filter, 250ms debounce)
     - TDS library filter drawer (mobile) open/close
     - Active-filter chips removal (URL nav — full reload)
     - Sort dropdown change → URL nav
     - Event #16 tds_library_viewed — page_load + filter_change + sort_change + search
     - Event #17 case_study_viewed — page_load on individual case study page
     - Case study page primary CTA (Request Quote on system) — multi-product placeholder
     - Per-product Add to Quote (Products Used section)
*/
(function () {
  'use strict';

  // Wait for window.icaEvents to be available (set by ica-events.js, deferred).
  function fireEvent(eventType, payload) {
    if (window.icaEvents && typeof window.icaEvents.fire === 'function') {
      window.icaEvents.fire(eventType, payload);
      return true;
    }
    return false;
  }
  function fireEventWhenReady(eventType, payload, attempt) {
    attempt = attempt || 0;
    if (fireEvent(eventType, payload)) return;
    if (attempt < 30) {
      setTimeout(function () { fireEventWhenReady(eventType, payload, attempt + 1); }, 100);
    }
  }

  function emailValid(s) {
    return typeof s === 'string' && s.indexOf('@') > 0 && s.lastIndexOf('.') > s.indexOf('@');
  }

  // ============================================================
  // TDS LIBRARY
  // ============================================================
  function initTdsLibrary() {
    var lib = document.getElementById('ica-tds-library');
    if (!lib) return;

    // -------- Read initial state for #16 page-load event --------
    function readActiveFilters() {
      var node = document.getElementById('ica-tds-library-state');
      if (!node) return { brand: [], product_line: [], industry: [] };
      try {
        var data = JSON.parse(node.textContent || node.innerText || '{}');
        return {
          brand: data.brand || [],
          product_line: data.product_line || [],
          industry: data.industry || []
        };
      } catch (e) {
        return { brand: [], product_line: [], industry: [] };
      }
    }
    function readResultCount() {
      var rows = lib.querySelectorAll('.ica-tds-row:not([hidden])');
      return rows.length;
    }
    function readSearchQuery() {
      var input = document.getElementById('ica-tds-search');
      return (input && input.value && input.value.trim()) || null;
    }

    // Fire page_load event
    fireEventWhenReady('tds_library_viewed', {
      source_trigger: 'page_load',
      active_filters: readActiveFilters(),
      search_query: readSearchQuery(),
      result_count: readResultCount()
    });

    // -------- Filter drawer toggle (mobile) --------
    var rail = document.getElementById('ica-tds-filter-rail');
    var openBtn = document.getElementById('ica-tds-filter-toggle');
    var closeBtn = document.getElementById('ica-tds-filter-rail-close');
    function openRail() {
      if (!rail) return;
      rail.classList.add('is-open');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }
    function closeRail() {
      if (!rail) return;
      rail.classList.remove('is-open');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
    if (openBtn) openBtn.addEventListener('click', openRail);
    if (closeBtn) closeBtn.addEventListener('click', closeRail);

    // Collapse dimensions on header click. aria-expanded lives on the BUTTON
    // (per WCAG — only interactive elements may carry the expand state).
    // The wrapper gets data-expanded for CSS-only styling hooks.
    lib.querySelectorAll('.ica-filter-dim-head').forEach(function (h) {
      h.addEventListener('click', function () {
        var dim = h.parentElement;
        if (!dim) return;
        var current = h.getAttribute('aria-expanded');
        var next = current === 'false' ? 'true' : 'false';
        h.setAttribute('aria-expanded', next);
        dim.setAttribute('data-expanded', next);
      });
    });

    // -------- Sort dropdowns → URL nav --------
    function navWithSort(value) {
      try {
        var url = new URL(window.location.href);
        if (value && value !== 'alpha') {
          url.searchParams.set('sort', value);
        } else {
          url.searchParams.delete('sort');
        }
        // Fire #16 sort_change BEFORE navigating
        fireEvent('tds_library_viewed', {
          source_trigger: 'sort_change',
          active_filters: readActiveFilters(),
          search_query: readSearchQuery(),
          result_count: readResultCount()
        });
        window.location.href = url.toString();
      } catch (e) { /* silent */ }
    }
    lib.querySelectorAll('.ica-tds-sort-select').forEach(function (sel) {
      sel.addEventListener('change', function () { navWithSort(sel.value); });
    });

    // -------- Filter chip click → fire #16 filter_change BEFORE link nav --------
    lib.querySelectorAll('.ica-filter-chip').forEach(function (chip) {
      chip.addEventListener('click', function () {
        // Capture state BEFORE the navigation flips the filter
        fireEvent('tds_library_viewed', {
          source_trigger: 'filter_change',
          active_filters: readActiveFilters(),
          search_query: readSearchQuery(),
          result_count: readResultCount()
        });
        // Default <a href> nav proceeds.
      });
    });
    lib.querySelectorAll('.ica-active-filter-chip, .ica-active-filter-clear').forEach(function (chip) {
      chip.addEventListener('click', function () {
        fireEvent('tds_library_viewed', {
          source_trigger: 'filter_change',
          active_filters: readActiveFilters(),
          search_query: readSearchQuery(),
          result_count: readResultCount()
        });
      });
    });

    // -------- Search (client-side, debounce 250ms) --------
    var searchInput = document.getElementById('ica-tds-search');
    var searchClear = document.getElementById('ica-tds-search-clear');
    var resultCountMobile = document.getElementById('ica-tds-result-count-mobile');
    var resultCountDesktop = document.getElementById('ica-tds-result-count-desktop');
    var emptyState = document.getElementById('ica-tds-empty');
    var searchTimer = null;
    function applySearch() {
      var query = (searchInput && searchInput.value || '').trim().toLowerCase();
      var totalRows = lib.querySelectorAll('.ica-tds-row');
      var visible = 0;
      totalRows.forEach(function (row) {
        // Skip rows already hidden by server-side filter
        if (row.dataset.filteredOut === 'true') return;
        if (!query) {
          row.hidden = false;
          visible++;
          return;
        }
        var hay = (row.dataset.search || '').toLowerCase();
        var match = hay.indexOf(query) !== -1;
        row.hidden = !match;
        if (match) visible++;
      });
      // Update counts
      var totalUnfiltered = lib.querySelectorAll('.ica-tds-row:not([data-filtered-out="true"])').length;
      if (resultCountMobile) {
        var strongs = resultCountMobile.querySelectorAll('strong');
        if (strongs[0]) strongs[0].textContent = visible;
        if (strongs[1]) strongs[1].textContent = totalUnfiltered;
      }
      if (resultCountDesktop) {
        var strongs2 = resultCountDesktop.querySelectorAll('strong');
        if (strongs2[0]) strongs2[0].textContent = visible;
        if (strongs2[1]) strongs2[1].textContent = totalUnfiltered;
      }
      if (emptyState) {
        if (visible === 0) { emptyState.classList.add('is-visible'); }
        else { emptyState.classList.remove('is-visible'); }
      }
      // Fire #16 search event
      fireEvent('tds_library_viewed', {
        source_trigger: 'search',
        active_filters: readActiveFilters(),
        search_query: query || null,
        result_count: visible
      });
    }
    if (searchInput) {
      searchInput.addEventListener('input', function () {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(applySearch, 250);
      });
    }
    if (searchClear) {
      searchClear.addEventListener('click', function () {
        if (searchInput) searchInput.value = '';
        applySearch();
        if (searchInput) searchInput.focus();
      });
    }

    // -------- TDS Download — direct (logged-in customer) --------
    lib.querySelectorAll('[data-ica-tds-direct]').forEach(function (a) {
      a.addEventListener('click', function () {
        fireEvent('tds_downloaded', {
          source: 'tds_library',
          product_handle: a.getAttribute('data-product-handle') || '',
          product_gid: a.getAttribute('data-product-gid') || '',
          sku: a.getAttribute('data-sku') || '',
          brand: a.getAttribute('data-brand') || '',
          product_line: a.getAttribute('data-product-line') || '',
          tds_file_url: a.getAttribute('href') || ''
        });
      });
    });

    // -------- TDS Download — guest gate flow --------
    lib.querySelectorAll('[data-ica-tds-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var rowId = btn.getAttribute('data-row-id');
        if (!rowId) return;
        var actionEl = document.getElementById('ica-tds-action-' + rowId);
        var gateEl = document.getElementById('ica-tds-gate-' + rowId);
        if (actionEl) actionEl.style.display = 'none';
        if (gateEl) {
          gateEl.classList.add('is-active');
          var emailEl = gateEl.querySelector('input[type="email"]');
          setTimeout(function () { if (emailEl) emailEl.focus(); }, 10);
        }
      });
    });
    lib.querySelectorAll('[data-ica-tds-cancel]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var rowId = btn.getAttribute('data-row-id');
        if (!rowId) return;
        var actionEl = document.getElementById('ica-tds-action-' + rowId);
        var gateEl = document.getElementById('ica-tds-gate-' + rowId);
        if (gateEl) gateEl.classList.remove('is-active');
        if (actionEl) actionEl.style.display = '';
        var btnEl = document.querySelector('[data-ica-tds-btn][data-row-id="' + rowId + '"]');
        if (btnEl) btnEl.focus();
      });
    });
    lib.querySelectorAll('.ica-tds-gate').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var rowId = form.getAttribute('data-row-id');
        var emailEl = form.querySelector('input[type="email"]');
        var email = (emailEl && emailEl.value || '').trim();
        if (!emailValid(email)) { if (emailEl) emailEl.focus(); return; }
        var tdsUrl = form.getAttribute('data-tds-url') || '';
        fireEvent('tds_downloaded', {
          source: 'tds_library',
          product_handle: form.getAttribute('data-product-handle') || '',
          product_gid: form.getAttribute('data-product-gid') || '',
          sku: form.getAttribute('data-sku') || '',
          brand: form.getAttribute('data-brand') || '',
          product_line: form.getAttribute('data-product-line') || '',
          email: email,
          tds_file_url: tdsUrl
        });
        // Hide form, show success
        form.classList.remove('is-active');
        var successEl = document.getElementById('ica-tds-success-' + rowId);
        if (successEl) {
          var emailSpan = successEl.querySelector('[data-success-email]');
          if (emailSpan) emailSpan.textContent = email;
          successEl.classList.add('is-active');
        }
        // Open PDF in new tab
        if (tdsUrl) {
          try { window.open(tdsUrl, '_blank', 'noopener'); } catch (err) { /* silent */ }
        }
      });
    });
  }

  // ============================================================
  // CASE STUDY PAGE — #17 case_study_viewed
  // ============================================================
  function initCaseStudyPage() {
    var node = document.getElementById('ica-case-study-event-payload');
    if (!node) return;
    var data;
    try { data = JSON.parse(node.textContent || node.innerText || '{}'); }
    catch (e) { return; }
    if (!data || !data.case_study_handle) return;

    // Resolve source: ?from= param wins, else referrer heuristic.
    // Allowed source values (aligned with Strategist spec): pdp_cross_link, hub, search, direct.
    var source = 'direct';
    var allowed = { pdp_cross_link: 1, hub: 1, search: 1, direct: 1 };
    try {
      var params = new URLSearchParams(window.location.search);
      var fromParam = params.get('from');
      if (fromParam && allowed[fromParam]) {
        source = fromParam;
      } else {
        var ref = document.referrer || '';
        if (ref.indexOf('/products/') !== -1) source = 'pdp_cross_link';
        else if (ref.indexOf('/pages/case-studies') !== -1) source = 'hub';
        else if (ref.indexOf('/search') !== -1) source = 'search';
      }
    } catch (e) { /* silent */ }

    fireEventWhenReady('case_study_viewed', {
      case_study_handle: data.case_study_handle,
      client_name: data.client_name || null,
      industry_handle: data.industry_handle || null,
      products_referenced: data.products_referenced || [],
      source: source
    });

    // Primary CTA — Request Quote on system (adds all products to Quote-Cart).
    var sysBtn = document.getElementById('ica-csp-request-quote-system-btn');
    if (sysBtn) {
      sysBtn.addEventListener('click', function () {
        var handles = (sysBtn.getAttribute('data-products') || '').split(',').filter(Boolean);
        handles.forEach(function (handle) {
          fireEvent('quote_basket_add', {
            product_handle: handle,
            source_page: 'case_study'
          });
        });
        // Phase 1: visual confirmation. Phase 2: opens Quote-Cart drawer
        // and fires `quote_basket_view`. The drawer code lives in ica-quote.js.
        var orig = sysBtn.innerHTML;
        sysBtn.innerHTML = 'Added ' + handles.length + ' products to quote ✓';
        sysBtn.style.background = 'var(--ica-color-success)';
        setTimeout(function () {
          sysBtn.innerHTML = orig;
          sysBtn.style.background = '';
        }, 2000);
        // Open Quote-Cart drawer if available.
        if (window.icaQuote && typeof window.icaQuote.open === 'function') {
          window.icaQuote.open({ source: 'case_study' });
        }
      });
    }

    // Per-product Add to Quote in Products Used section.
    document.querySelectorAll('[data-ica-add-to-quote]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var handle = btn.getAttribute('data-product-handle') || '';
        fireEvent('quote_basket_add', {
          product_handle: handle,
          source_page: 'case_study'
        });
        var orig = btn.innerHTML;
        btn.innerHTML = 'Added ✓';
        btn.style.background = 'var(--ica-color-success)';
        setTimeout(function () {
          btn.innerHTML = orig;
          btn.style.background = '';
        }, 1500);
      });
    });
  }

  // ============================================================
  // BOOT
  // ============================================================
  function boot() {
    initTdsLibrary();
    initCaseStudyPage();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
